import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import MuiDialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import type { DialogProps as MuiDialogProps } from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import {
  forwardRef,
  useId,
  type JSX,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'

import {
  BottomActionBar,
  type BottomActionBarProps,
} from '../../surfaces/bottom-action-bar'

import { Divider } from '../../data-display/divider/Divider'
import { DialogTitle } from './dialog-title/DialogTitle'

export interface DialogProps
  extends Pick<MuiDialogProps, 'slotProps' | 'maxWidth' | 'fullWidth' | 'sx'> {
  title: JSX.Element | string
  titleWithBorderBottom?: boolean
  header?: ReactElement
  content?: JSX.Element
  actions?: ReactNode
  bottomActions?: BottomActionBarProps
  children?: ReactNode
  isOpen: boolean
  variant?: 'default' | 'fullScreenOnMobile' | 'bottomSheet' | 'fullScreen'
  withGutters?: boolean
  onClose?: () => void
  closeDisabled?: boolean
  'data-testid'?: string
  scroll?: 'paper' | 'body'
}

const UpTransition = forwardRef(
  (
    props: TransitionProps & { children: ReactElement<unknown> },
    ref: Ref<unknown>,
  ) => {
    return (
      <Slide direction="up" ref={ref} {...props}>
        {props.children}
      </Slide>
    )
  },
)

export const Dialog = ({
  title,
  titleWithBorderBottom = false,
  header,
  content,
  actions,
  bottomActions,
  isOpen = false,
  variant = 'default',
  withGutters = true,
  onClose,
  closeDisabled = false,
  scroll,
  slotProps,
  'data-testid': dataTestId,
  ...props
}: DialogProps): JSX.Element => {
  const theme = useTheme()
  const isFullScreen = variant === 'fullScreen'
  const fullScreen = variant === 'fullScreenOnMobile'
  const isBottomSheet = variant === 'bottomSheet'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const titleId = useId()
  const contentId = useId()

  const paperSlotProps =
    typeof slotProps?.paper === 'function' ? undefined : slotProps?.paper
  const resolveSxObject = (currentSx: unknown): Record<string, unknown> => {
    if (currentSx == null) {
      return {}
    }
    if (typeof currentSx === 'function') {
      return resolveSxObject(currentSx(theme))
    }
    if (Array.isArray(currentSx)) {
      return currentSx.reduce<Record<string, unknown>>((acc, item) => {
        return {
          ...acc,
          ...resolveSxObject(item),
        }
      }, {})
    }
    return currentSx as Record<string, unknown>
  }

  return (
    <MuiDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={content ? contentId : undefined}
      fullScreen={isFullScreen || (isMobile && fullScreen)}
      slots={{
        transition: isBottomSheet && isMobile ? UpTransition : undefined,
      }}
      slotProps={{
        ...slotProps,
        paper: {
          ...paperSlotProps,
          sx: {
            p: 0,
            ...(isBottomSheet &&
              isMobile && {
                position: 'absolute',
                width: '100%',
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                bottom: 0,
                m: 0,
                pb: 2,
              }),
            ...resolveSxObject(paperSlotProps?.sx),
          },
          className: paperSlotProps?.className,
        },
      }}
      data-testid={dataTestId}
      scroll={scroll}
      fullWidth
      {...props}
    >
      <DialogTitle
        id={titleId}
        onClose={onClose}
        closeDisabled={closeDisabled}
        title={title}
        withSeparator={titleWithBorderBottom}
      >
        {header}
      </DialogTitle>
      <Divider />
      {content && (
        <DialogContent id={contentId} sx={withGutters ? { p: 2 } : undefined}>
          {content}
        </DialogContent>
      )}

      {(actions || bottomActions) && (
        <DialogActions sx={{ p: 2 }}>
          {actions ?? <BottomActionBar {...bottomActions} />}
        </DialogActions>
      )}
    </MuiDialog>
  )
}
